import { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import {
  api,
  ApiError,
  type OtherAccountSummary,
  type SignInMethods,
  type WhatsAppStart,
  type WhatsAppStatus,
} from './api';

/**
 * What a finished QR hands back. A conflict carries the code and poll secret
 * with it, because settling it is a second request that needs both.
 */
export type WhatsAppResult =
  | Extract<WhatsAppStatus, { status: 'signed_in' | 'linked' | 'confirmed' }>
  | (Extract<WhatsAppStatus, { status: 'conflict' }> & { code: string; pollSecret: string });

/** How often the screen asks whether the WhatsApp message has arrived. */
const POLL_MS = 2000;

/** Mirrors PIN_RE on the server: all digits, 6 to 8 of them. */
function isPin(secret: string): boolean {
  return /^[0-9]{6,8}$/.test(secret);
}

const ERROR_MESSAGES: Record<string, string> = {
  account_disabled:
    'That account has been deactivated. Ask an administrator to switch it back on.',
  phone_in_use: 'That WhatsApp number is already linked to a different Billdedo account.',
};

/**
 * A WhatsApp QR and the wait for it to be sent.
 *
 * Shared by sign-in and by "Link WhatsApp" in the account menu: the only
 * difference is which endpoint mints the code, so that is passed in as
 * `start`. Whatever the server finally answers — a session, or a linked
 * phone — goes to `onResult`.
 *
 * A phone cannot scan its own screen, so the "Open WhatsApp" button carries
 * the same link as the QR. On a phone it is the only thing that matters; the
 * QR is for the laptop.
 */
export function WhatsAppQrPanel({
  start,
  onResult,
  onCancel,
}: {
  start: () => Promise<WhatsAppStart>;
  onResult: (r: WhatsAppResult) => void;
  onCancel?: () => void;
}) {
  const [login, setLogin] = useState<WhatsAppStart | null>(null);
  const [qr, setQr] = useState('');
  const [state, setState] = useState<'loading' | 'waiting' | 'expired' | 'error'>('loading');
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  // The result callback changes identity on every parent render; the poll
  // loop must not restart because of it.
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const begin = useCallback(async () => {
    setState('loading');
    setError('');
    setQr('');
    try {
      const l = await start();
      setLogin(l);
      setQr(await QRCode.toDataURL(l.waUrl, { width: 240, margin: 1, errorCorrectionLevel: 'M' }));
      setState('waiting');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the portal');
      setState('error');
    }
  }, [start]);

  useEffect(() => {
    void begin();
  }, [begin]);

  // Poll while a code is live. Stops on any answer but "pending".
  useEffect(() => {
    if (state !== 'waiting' || !login) return;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      setNow(Date.now());
      try {
        const r = await api.whatsappStatus(login.code, login.pollSecret);
        if (stopped) return;
        if (r.status === 'pending') return;
        if (r.status === 'expired') return setState('expired');
        if (r.status === 'error') {
          setError(ERROR_MESSAGES[r.error] ?? `WhatsApp sign-in failed (${r.error}).`);
          return setState('error');
        }
        stopped = true;
        onResultRef.current(
          r.status === 'conflict' ? { ...r, code: login.code, pollSecret: login.pollSecret } : r,
        );
      } catch {
        // A dropped request is not a failed sign-in; the next tick retries.
      }
    };
    const id = window.setInterval(tick, POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [state, login]);

  const secondsLeft = login ? Math.max(0, Math.round((login.expiresAt - now) / 1000)) : 0;
  const message = login ? `login billdedo ${login.code}` : '';

  return (
    <div className="wa-panel">
      {state === 'loading' && (
        <div className="centre" style={{ minHeight: 200 }}>
          <div className="spinner" />
        </div>
      )}

      {state === 'waiting' && login && (
        <>
          <ol className="wa-steps">
            <li>Scan this code with your phone camera, or tap <strong>Open WhatsApp</strong>.</li>
            <li>WhatsApp opens with the message ready — tap <strong>Send</strong>.</li>
            <li>This page signs you in by itself.</li>
          </ol>
          {qr && <img className="wa-qr" src={qr} alt="WhatsApp sign-in QR code" width={240} height={240} />}
          <a className="btn wa" href={login.waUrl} target="_blank" rel="noopener noreferrer">
            <WhatsAppMark />
            Open WhatsApp
          </a>
          <p className="hint wa-manual">
            Or send <code>{message}</code> to <strong>+{login.number}</strong>.
            <br />
            Waiting for your message… {Math.floor(secondsLeft / 60)}:
            {String(secondsLeft % 60).padStart(2, '0')}
          </p>
        </>
      )}

      {state === 'expired' && (
        <div className="banner warn">
          That code has expired.{' '}
          <button type="button" className="btn sm" onClick={() => void begin()}>
            Get a new code
          </button>
        </div>
      )}

      {state === 'error' && (
        <div className="banner bad">
          {error}{' '}
          <button type="button" className="btn sm" onClick={() => void begin()}>
            Try again
          </button>
        </div>
      )}

      {onCancel && (
        <button type="button" className="btn ghost sm" onClick={onCancel} style={{ marginTop: 8 }}>
          Cancel
        </button>
      )}
    </div>
  );
}

/** Where the dashboard was sent back to after a Google link round trip. */
export type GoogleLinkReturn =
  | { kind: 'linked'; email: string }
  | { kind: 'error'; reason: string }
  | { kind: 'conflict'; token: string };

const GOOGLE_LINK_ERRORS: Record<string, string> = {
  cancelled: 'Google linking was cancelled.',
  email_unverified: 'That Google account has no verified email address.',
  account_disabled: 'This account has been deactivated.',
  state_mismatch: 'The link expired or was tampered with. Try again.',
  exchange_failed: 'Google could not complete the link. Try again.',
};

type Conflict =
  | { via: 'whatsapp'; label: string; other: OtherAccountSummary; code: string; pollSecret: string }
  | { via: 'google'; label: string; other: OtherAccountSummary; token: string };

/**
 * Every way into this account — a WhatsApp number, a Google account — and
 * the means to add the one it lacks.
 *
 * Linking is what lets somebody use either and land on the same account.
 * When the number or Google account being linked already has a Billdedo
 * account of its own, the drawer asks whether to merge that account in or
 * delete it (ConflictChoice) before anything changes.
 */
export function SignInMethodsDrawer({
  onClose,
  googleReturn,
  passwordConfirm,
}: {
  onClose: () => void;
  /** Set when Google has just sent the browser back from a link. */
  googleReturn?: GoogleLinkReturn | null;
  /** Set when Google has just sent the browser back from a re-confirmation. */
  passwordConfirm?: { kind: 'ok'; token: string } | { kind: 'error'; reason: string } | null;
}) {
  const [methods, setMethods] = useState<SignInMethods | null>(null);
  const [linkingPhone, setLinkingPhone] = useState(false);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  /**
   * Where the password flow is.
   *
   *   null        the row is just showing whether one is set
   *   'confirm'   asking the person to prove it is still them
   *   'waiting'   the WhatsApp QR is up
   *   token       confirmed; the password fields are open
   */
  const [pwStep, setPwStep] = useState<null | 'confirm' | 'waiting'>(null);
  const [confirmToken, setConfirmToken] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  /** Set, or remove — decided when the flow starts, so the end knows what to do. */
  const [pwIntent, setPwIntent] = useState<'set' | 'remove'>('set');

  const reload = useCallback(() => {
    void api
      .signInMethods()
      .then(setMethods)
      .catch(() =>
        setMethods({
          phone: null,
          google: null,
          password: false,
          available: { whatsapp: false, google: false, password: false },
        }),
      );
  }, []);

  useEffect(reload, [reload]);

  // Back from Google: say how it went, or ask about the account it collided with.
  useEffect(() => {
    if (!googleReturn) return;
    if (googleReturn.kind === 'linked') {
      setMsg({ ok: true, text: `Linked Google account ${googleReturn.email}.` });
    } else if (googleReturn.kind === 'error') {
      setMsg({
        ok: false,
        text: GOOGLE_LINK_ERRORS[googleReturn.reason] ?? `Google linking failed (${googleReturn.reason}).`,
      });
    } else {
      void api
        .googleConflict(googleReturn.token)
        .then((r) => {
          if (r.status === 'conflict') {
            setConflict({ via: 'google', label: r.email, other: r.other, token: googleReturn.token });
          } else {
            setMsg({ ok: true, text: `Linked Google account ${r.email}.` });
          }
        })
        .catch((err) =>
          setMsg({ ok: false, text: err instanceof ApiError ? err.message : 'Could not reach the portal' }),
        );
    }
  }, [googleReturn]);

  async function unlinkPhone() {
    if (!confirm('Unlink WhatsApp from this account? You will no longer be able to sign in with it.')) return;
    try {
      await api.unlinkWhatsApp();
      setMsg({ ok: true, text: 'WhatsApp unlinked.' });
      reload();
    } catch (err) {
      setMsg({ ok: false, text: err instanceof ApiError ? err.message : 'Could not unlink' });
    }
  }

  // Back from Google with proof (or without it).
  useEffect(() => {
    if (!passwordConfirm) return;
    if (passwordConfirm.kind === 'ok') {
      setConfirmToken(passwordConfirm.token);
      setPwStep(null);
    } else {
      setMsg({
        ok: false,
        text:
          passwordConfirm.reason === 'wrong_account'
            ? 'That is a different Google account from the one on this login.'
            : 'Could not confirm it was you. Try again.',
      });
    }
  }, [passwordConfirm]);

  async function confirmWithGoogle() {
    try {
      const { url } = await api.confirmPasswordWithGoogle();
      window.location.assign(url);
    } catch (err) {
      setMsg({ ok: false, text: err instanceof ApiError ? err.message : 'Could not reach the portal' });
    }
  }

  async function savePassword() {
    if (pw1 !== pw2) {
      setMsg({ ok: false, text: 'Those two passwords are not the same.' });
      return;
    }
    setPwBusy(true);
    setMsg(null);
    try {
      if (pwIntent === 'remove') {
        await api.removePassword(confirmToken);
        setMsg({ ok: true, text: 'Password removed. Google and WhatsApp still work.' });
      } else {
        await api.setPassword(pw1, confirmToken);
        setMsg({ ok: true, text: 'Password saved. You can sign in with it next time.' });
      }
      setConfirmToken('');
      setPw1('');
      setPw2('');
      reload();
    } catch (err) {
      setMsg({ ok: false, text: err instanceof ApiError ? err.message : 'Could not save it' });
    } finally {
      setPwBusy(false);
    }
  }

  async function linkGoogle() {
    try {
      const { url } = await api.linkGoogle();
      window.location.assign(url);
    } catch (err) {
      setMsg({ ok: false, text: err instanceof ApiError ? err.message : 'Could not reach the portal' });
    }
  }

  const busyElsewhere = linkingPhone || !!conflict || pwStep === 'waiting';

  return (
    <div className="scrim" onClick={onClose}>
      <div className="card drawer" onClick={(e) => e.stopPropagation()}>
        <h2>Sign-in methods</h2>
        {msg && <div className={`banner ${msg.ok ? 'ok' : 'bad'}`}>{msg.text}</div>}

        {!methods && <div className="spinner" />}

        {conflict && (
          <ConflictChoice
            conflict={conflict}
            onCancel={() => setConflict(null)}
            onDone={(text) => {
              setConflict(null);
              setMsg({ ok: true, text });
              reload();
            }}
          />
        )}

        {pwStep === 'waiting' && (
          <WhatsAppQrPanel
            start={api.confirmPasswordWithWhatsApp}
            onCancel={() => setPwStep('confirm')}
            onResult={(r) => {
              if (r.status === 'confirmed') {
                setConfirmToken(r.confirmToken);
                setPwStep(null);
              } else {
                // Only a 'confirmed' is proof. Anything else means the phone
                // that answered was not the one on this account.
                setPwStep(null);
                setMsg({ ok: false, text: 'That is a different number from the one on this login.' });
              }
            }}
          />
        )}

        {linkingPhone && (
          <WhatsAppQrPanel
            start={api.linkWhatsApp}
            onCancel={() => setLinkingPhone(false)}
            onResult={(r) => {
              setLinkingPhone(false);
              if (r.status === 'linked') {
                setMsg({ ok: true, text: `Linked +${r.phone}.` });
                reload();
              }
              if (r.status === 'conflict') {
                setMsg(null);
                setConflict({
                  via: 'whatsapp',
                  label: `+${r.phone}`,
                  other: r.other,
                  code: r.code,
                  pollSecret: r.pollSecret,
                });
              }
            }}
          />
        )}

        {methods && !busyElsewhere && (
          <div className="signin-methods">
            <div className="signin-row">
              <WhatsAppMark />
              <div className="signin-what">
                <strong>WhatsApp</strong>
                <span className="hint">{methods.phone ? `+${methods.phone}` : 'Not linked'}</span>
              </div>
              {methods.phone ? (
                <button className="btn sm" onClick={unlinkPhone}>
                  Unlink
                </button>
              ) : (
                <button
                  className="btn sm primary"
                  disabled={!methods.available.whatsapp}
                  title={methods.available.whatsapp ? '' : 'WhatsApp sign-in is not set up on this portal'}
                  onClick={() => {
                    setMsg(null);
                    setLinkingPhone(true);
                  }}
                >
                  Link number
                </button>
              )}
            </div>

            <div className="signin-row">
              <GoogleG />
              <div className="signin-what">
                <strong>Google</strong>
                <span className="hint">
                  {methods.google ? methods.google.email ?? 'Linked' : 'Not linked'}
                </span>
              </div>
              {!methods.google && (
                <button
                  className="btn sm primary"
                  disabled={!methods.available.google}
                  title={methods.available.google ? '' : 'Google sign-in is not set up on this portal'}
                  onClick={() => void linkGoogle()}
                >
                  Link Google
                </button>
              )}
            </div>

            <div className="signin-row">
              <span aria-hidden="true" style={{ fontSize: 18, width: 22, textAlign: 'center' }}>🔑</span>
              <div className="signin-what">
                <strong>Password</strong>
                <span className="hint">
                  {!methods.available.password
                    ? 'Not available on this portal'
                    : methods.password
                      ? 'Set — sign in with your email or phone'
                      : 'Not set'}
                </span>
              </div>
              {methods.available.password && !confirmToken && pwStep === null && (
                <button
                  className="btn sm primary"
                  disabled={busyElsewhere}
                  onClick={() => {
                    setMsg(null);
                    setPwIntent('set');
                    setPwStep('confirm');
                  }}
                >
                  {methods.password ? 'Change' : 'Set password'}
                </button>
              )}
            </div>

            {/*
              Proving it is still you, before a password is written.
              A session lasts twelve hours and may be in front of somebody
              else by now; the password it sets would outlive it.
            */}
            {pwStep === 'confirm' && (
              <div className="panel" style={{ display: 'grid', gap: 10, padding: 12 }}>
                <strong style={{ fontSize: 13 }}>First, confirm it is you</strong>
                <span className="hint">
                  Sign in again with the account already on this login. It takes a moment
                  and keeps somebody at a borrowed screen from setting a password on your
                  account.
                </span>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {methods.google && methods.available.google && (
                    <button className="btn sm" onClick={() => void confirmWithGoogle()}>
                      Confirm with Google
                    </button>
                  )}
                  {methods.phone && methods.available.whatsapp && (
                    <button className="btn sm" onClick={() => setPwStep('waiting')}>
                      Confirm with WhatsApp
                    </button>
                  )}
                  <button className="btn sm ghost" onClick={() => setPwStep(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {confirmToken && (
              <div className="panel" style={{ display: 'grid', gap: 10, padding: 12 }}>
                <strong style={{ fontSize: 13 }}>
                  {pwIntent === 'remove' ? 'Remove your password' : 'Choose a password or PIN'}
                </strong>
                {pwIntent === 'remove' ? (
                  <span className="hint">
                    Google and WhatsApp will still sign you in. You can set a new password
                    whenever you like.
                  </span>
                ) : (
                  <>
                    <span className="hint">
                      A password of at least 8 characters with a letter and a number, or a
                      PIN of 6 to 8 digits.
                    </span>
                    {/*
                      Said plainly rather than left for the person to work out. A PIN is
                      the weaker of the two and the difference is large — eight digits is
                      a hundred million combinations against an eight-character
                      password's two hundred trillion — so the one number that buys the
                      most is the one worth naming.
                    */}
                    {isPin(pw1) && (
                      <span className="hint" style={{ color: 'var(--warn, #b45309)' }}>
                        {pw1.length < 8
                          ? `A ${pw1.length}-digit PIN. Eight digits is a hundred times harder to guess — worth the extra taps.`
                          : 'An 8-digit PIN. Strongest of the PIN lengths.'}
                      </span>
                    )}
                    <input
                      type="password"
                      value={pw1}
                      onChange={(e) => setPw1(e.target.value)}
                      autoComplete="new-password"
                      placeholder="New password or PIN"
                    />
                    <input
                      type="password"
                      value={pw2}
                      onChange={(e) => setPw2(e.target.value)}
                      autoComplete="new-password"
                      placeholder="Type it again"
                    />
                    {/*
                      Only once the second entry has actually diverged, not
                      from its first character: a warning that appears while
                      somebody is still typing the right thing is noise, and
                      people learn to look past it.
                    */}
                    {pw2 !== '' && !pw1.startsWith(pw2) && (
                      <span className="hint" style={{ color: 'var(--bad, #dc2626)' }}>
                        These two do not match.
                      </span>
                    )}
                  </>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    className="btn sm primary"
                    disabled={pwBusy || (pwIntent === 'set' && (!pw1 || !pw2 || pw1 !== pw2))}
                    onClick={() => void savePassword()}
                  >
                    {pwBusy
                      ? 'Saving…'
                      : pwIntent === 'remove'
                        ? 'Remove it'
                        : isPin(pw1)
                          ? 'Save PIN'
                          : 'Save password'}
                  </button>
                  {pwIntent === 'set' && methods.password && (
                    <button className="btn sm ghost" onClick={() => setPwIntent('remove')}>
                      Remove instead
                    </button>
                  )}
                  <button
                    className="btn sm ghost"
                    onClick={() => {
                      setConfirmToken('');
                      setPw1('');
                      setPw2('');
                      setPwIntent('set');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="actions">
              <button className="btn ghost" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The number or Google account being linked already has its own Billdedo
 * account. Fold it into this one, or delete it.
 *
 * Merge is listed first because it loses nothing. Delete is only offered when
 * that account owns no restaurants, since deleting it would leave them with
 * no owner — the server refuses it too.
 */
function ConflictChoice({
  conflict,
  onCancel,
  onDone,
}: {
  conflict: Conflict;
  onCancel: () => void;
  onDone: (message: string) => void;
}) {
  const [busy, setBusy] = useState<'merge' | 'delete' | null>(null);
  const [error, setError] = useState('');
  const o = conflict.other;

  async function choose(action: 'merge' | 'delete') {
    const question =
      action === 'merge'
        ? `Merge "${o.name}" into this account?\n\nIts restaurants and memberships move here, then that account is deleted.`
        : `Delete "${o.name}"?\n\nThis cannot be undone.`;
    if (!confirm(question)) return;
    setBusy(action);
    setError('');
    try {
      if (conflict.via === 'whatsapp') {
        await api.resolveWhatsApp(conflict.code, conflict.pollSecret, action);
      } else {
        await api.resolveGoogle(conflict.token, action);
      }
      onDone(
        action === 'merge'
          ? `Merged the other account into this one and linked ${conflict.label}.`
          : `Deleted the other account and linked ${conflict.label}.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the portal');
      setBusy(null);
    }
  }

  const owns =
    o.restaurantsOwned === 0
      ? 'owns no restaurants'
      : `owns ${o.restaurantsOwned} restaurant${o.restaurantsOwned === 1 ? '' : 's'}` +
        (o.restaurantNames.length
          ? ` (${o.restaurantNames.join(', ')}${o.restaurantsOwned > o.restaurantNames.length ? ', …' : ''})`
          : '');
  const known = [o.email, o.phone ? `+${o.phone}` : null].filter(Boolean).join(' · ');

  return (
    <div>
      <div className="banner warn">
        <strong>{conflict.label}</strong> already has its own Billdedo account.
      </div>
      <p className="sub">
        <strong>{o.name}</strong>
        {known ? ` · ${known}` : ''} — {owns}
        {o.memberships
          ? `, and is on ${o.memberships} other restaurant${o.memberships === 1 ? '' : 's'}`
          : ''}
        .
      </p>
      <p className="sub">To link it here, choose what happens to that account:</p>
      {error && <div className="banner bad">{error}</div>}

      {!o.canMerge && (
        <div className="banner bad">
          That belongs to a Scandeer or reseller account, so it cannot be merged or deleted from
          here. Link a different one, or ask a Scandeer administrator.
        </div>
      )}

      <div className="actions" style={{ flexWrap: 'wrap' }}>
        {o.canMerge && (
          <button className="btn primary" disabled={!!busy} onClick={() => void choose('merge')}>
            {busy === 'merge' ? 'Merging…' : 'Merge into this account'}
          </button>
        )}
        {o.canMerge && (
          <button
            className="btn"
            disabled={!!busy || !o.canDelete}
            title={o.canDelete ? '' : 'It owns restaurants — merge it instead so they move here'}
            onClick={() => void choose('delete')}
          >
            {busy === 'delete' ? 'Deleting…' : 'Delete that account'}
          </button>
        )}
        <button className="btn ghost" disabled={!!busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
      {o.canMerge && !o.canDelete && (
        <p className="hint" style={{ marginTop: 8 }}>
          Delete is off because that account owns restaurants. Merging moves them to this account.
        </p>
      )}
    </div>
  );
}

/** Google's G, inline so nothing is fetched from another host. */
function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#4285F4" d="M45 24c0-1.6-.1-2.7-.4-4H24v7.5h12c-.2 2-1.6 5-4.5 7l6.9 5.3C42.5 36.2 45 30.6 45 24z" />
      <path fill="#34A853" d="M24 46c6 0 11-2 14.6-5.4l-6.9-5.3c-1.9 1.3-4.4 2.2-7.7 2.2-5.9 0-10.9-3.9-12.7-9.2l-7.1 5.5C7.7 41 15.3 46 24 46z" />
      <path fill="#FBBC05" d="M11.3 28.3A13.5 13.5 0 0 1 10.6 24c0-1.5.3-3 .7-4.3l-7.1-5.5A22 22 0 0 0 2 24c0 3.5.8 6.9 2.2 9.8z" />
      <path fill="#EA4335" d="M24 10.2c3.3 0 6.2 1.1 8.5 3.3l6.1-6.1C34.9 3.9 30 2 24 2 15.3 2 7.7 7 4.2 14.2l7.1 5.5C13.1 14.1 18.1 10.2 24 10.2z" />
    </svg>
  );
}

/** WhatsApp's mark, inline so nothing is fetched from another host. */
export function WhatsAppMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 32 32" aria-hidden="true">
      <path
        fill="#25D366"
        d="M16 3C9 3 3.3 8.7 3.3 15.7c0 2.5.7 4.8 1.9 6.8L3 29l6.7-2.1c1.9 1 4.1 1.6 6.3 1.6 7 0 12.7-5.7 12.7-12.7S23 3 16 3z"
      />
      <path
        fill="#fff"
        d="M22.6 18.9c-.3-.2-2-1-2.3-1.1-.3-.1-.5-.2-.8.2-.2.3-.9 1.1-1 1.3-.2.2-.4.3-.7.1-.3-.2-1.4-.5-2.7-1.7-1-.9-1.7-2-1.9-2.3-.2-.3 0-.5.1-.7l.5-.6c.2-.2.2-.4.3-.6.1-.2 0-.5 0-.6l-1-2.5c-.3-.7-.5-.6-.8-.6h-.6c-.2 0-.6.1-.9.4-.3.3-1.2 1.2-1.2 2.9s1.2 3.4 1.4 3.6c.2.2 2.4 3.7 5.9 5.2.8.4 1.5.6 2 .7.8.3 1.6.2 2.2.1.7-.1 2-.8 2.3-1.6.3-.8.3-1.5.2-1.6-.1-.2-.3-.3-.6-.4z"
      />
    </svg>
  );
}
